import { memo } from 'react';
import clsx from 'clsx';
import styles from './GstSummaryTable.module.scss';
import Tooltip from '../../../../../common/_custom/Tooltip/Tooltip';
import { formatAmountInUnit, isNegativeAmount } from '../RevenueVendorExpenseTable/components/overlap.constants';
import { getDataAvailabilityBadge } from './dataAvailability';
import { getSummaryCell } from './gstSummaryAdapter';
import { NOT_AVAILABLE_LABEL } from './constants';
import { SummaryRow } from './types';
import { GstConnectionStatus } from '../../../../../store/dashboard/revenueVendorExpenses/types';

interface AmountCellProps {
    row: SummaryRow;
    periodKey: string;
    returnCode: string;
    unitDivisor: number;
    isTotalColumn?: boolean;
    /** id of the table footnote that explains this cell's note marker. */
    footnoteId?: string;
}

const DISPLAY_DECIMALS_FACTOR = 100;

// A tiny negative rescaled into Lakhs/Crore rounds to "₹-0.00": show it as a plain zero amount instead.
const formatSummaryAmount = (value: number, unitDivisor: number): string => {
    const roundsToZero = Math.round(Math.abs(value / unitDivisor) * DISPLAY_DECIMALS_FACTOR) === 0;
    return formatAmountInUnit(roundsToZero ? 0 : value, unitDivisor);
};

/** One amount cell: null / missing renders the not-available label; 0 renders the normal zero amount. */
function SummaryAmountCellBase({ row, periodKey, returnCode, unitDivisor, isTotalColumn, footnoteId }: AmountCellProps) {
    const value = getSummaryCell(row, periodKey, returnCode);
    const isAvailable = value !== null;
    const text = isAvailable ? formatSummaryAmount(value, unitDivisor) : NOT_AVAILABLE_LABEL;
    const note = row.notes[returnCode];
    return (
        <td
            aria-describedby={note ? footnoteId : undefined}
            className={clsx(styles.Cell, {
                [styles.Bold]: row.isBold,
                [styles.NotAvailable]: !isAvailable,
                [styles.Negative]: isNegativeAmount(text),
                [styles.TotalColumn]: isTotalColumn,
            })}
        >
            {note ? (
                <Tooltip content={note} direction={'top'} fitContent small usePortal focusable ariaLabel={`${text}. ${note}`}>
                    <span className={styles.Noted} data-testid="gst-summary-note-cell">
                        {text}
                        <sup className={styles.NoteMarker} aria-describedby={footnoteId}>
                            *
                        </sup>
                    </span>
                </Tooltip>
            ) : (
                text
            )}
        </td>
    );
}

export const SummaryAmountCell = memo(SummaryAmountCellBase);

interface DataAvailableCellProps {
    monthKey: string;
    returnCode: string;
    availability: Record<string, Record<string, { gst: string; state: string | null; dataSource?: string | null }[]>>;
    gstConnectionStatus: GstConnectionStatus;
}

/** One x/y badge per return per month; x = GSTINs with data in that return, y = primary-status GSTINs plus any other GSTIN with data that month. */
function DataAvailableCellBase({ monthKey, returnCode, availability, gstConnectionStatus }: DataAvailableCellProps) {
    return (
        <td className={styles.Cell}>
            {getDataAvailabilityBadge({
                gsts: availability[returnCode]?.[monthKey],
                month: monthKey,
                gstList: gstConnectionStatus.totalGstList || [],
                gstMonthErrors: gstConnectionStatus.gstMonthErrors,
            })}
        </td>
    );
}

export const DataAvailableCell = memo(DataAvailableCellBase);
